import {
  DELIVERY_INTERVALS,
  PAYMENT_PARTNER_IDS,
  TIMEBLOCK_TYPE_IDS,
} from "@chef/constants";
import { Events } from "@chef/events";
import {
  formatTimeInterval,
  isGroceryBasketType,
  isMealboxBasketType,
  isMealboxProduct,
} from "@chef/helpers";
import { useBillingQuery, useTimeblocksQuery } from "@chef/state-management";
import { productAndVariationToTrackingData } from "@chef/state-management/helpers";
import { isEqualStrings } from "@chef/utils/equal";

export const useTrackingData = () => {
  const { data: billingData } = useBillingQuery();

  const mealboxBasket = billingData?.billing.baskets.find(isMealboxBasketType);

  const { data: timeblocksData } = useTimeblocksQuery(
    {
      timeblockTypeId: TIMEBLOCK_TYPE_IDS.NORMAL,
      postalCode:
        billingData?.billing?.addresses
          .find((address) =>
            isEqualStrings(address.addressId, mealboxBasket?.shippingAddressId),
          )
          ?.postalCode.toString() || "",
    },
    { skip: !billingData },
  );

  if (!billingData?.billing || !timeblocksData?.timeblocksData) {
    return undefined;
  }

  const billing = billingData.billing;

  const groceryBasket = billing.baskets.find(isGroceryBasketType);

  const paymentPartnerId = billing.paymentPartnerId;

  const selectedVariation = mealboxBasket?.basketProducts.find(
    (basketProduct) => isMealboxProduct(basketProduct.variation.product),
  )?.variation;

  const selectedProduct = selectedVariation?.product;

  const deliveryTimeblock = timeblocksData.timeblocksData.timeblocks.find(
    (timeblock) => timeblock.timeblockId === mealboxBasket?.timeblockId,
  );

  const addonTrackingProducts =
    groceryBasket?.basketProducts?.map((bp) => {
      const selectedVariation = bp.variation;
      const selectedProduct = selectedVariation.product;

      return productAndVariationToTrackingData({
        quantity: bp.quantity,
        selectedVariation,
        selectedProduct,
      });
    }) || [];

  const weekInfo = deliveryIntervalToTrackingData(
    mealboxBasket?.deliveryInterval,
  );

  let products: ReturnType<typeof productAndVariationToTrackingData>[] = [];
  if (selectedProduct && selectedVariation) {
    const productData = productAndVariationToTrackingData({
      selectedProduct,
      selectedVariation,
    });

    products = [productData, ...addonTrackingProducts];
  }

  return {
    payment_method: (paymentPartnerId === PAYMENT_PARTNER_IDS.COLLECTOR
      ? "invoice"
      : "cc") as "invoice" | "cc",
    status: billing.status,

    timeblock_day: deliveryTimeblock?.weekday,
    timeblock_id: deliveryTimeblock?.timeblockId,
    timeblock_interval: formatTimeInterval(
      deliveryTimeblock?.from,
      deliveryTimeblock?.to,
    ),

    week_type_id: weekInfo?.week_type_id,
    week_type_name: weekInfo?.week_type_name,

    products,
  };
};

const deliveryIntervalToTrackingData = (
  deliveryInterval?: number,
):
  | {
      week_type_id: Events["viewAccount"]["week_type_id"];
      week_type_name: Events["viewAccount"]["week_type_name"];
    }
  | undefined => {
  switch (deliveryInterval) {
    case DELIVERY_INTERVALS.EVERY_WEEK:
      return {
        week_type_id: "10",
        week_type_name: "Every week",
      };

    case DELIVERY_INTERVALS.EVEN_WEEKS:
      return {
        week_type_id: "20",
        week_type_name: "Even weeks",
      };

    case DELIVERY_INTERVALS.ODD_WEEKS:
      return {
        week_type_id: "30",
        week_type_name: "Odd weeks",
      };
  }

  return undefined;
};
